/**
 * The sprite format, and the rules that make bad data impossible to render.
 *
 * A spec is data. It never names a colour, never names a pixel coordinate it
 * cannot justify, and never reaches outside the vocabulary. Everything it can
 * get wrong is checked before a single pixel is drawn, and a mistake is an
 * error with a list of what was allowed, not a blank PNG that looks plausible
 * until someone opens the game.
 *
 * That last part is the whole reason this is safe to let a model author.
 */

import { SpecError } from "./error.js";
import { KNOWN_GLYPHS } from "./glyphs.js";
import { kitFrom, resolveRamp, resolveRole, type Kit, type KitSource } from "./kit.js";
import type { Rgba } from "./raster.js";

export const CURVES = ["linear", "pulse"] as const;
export type Curve = (typeof CURVES)[number];

export interface Frames {
	count: number;
	/** Name the frame position is exposed under, e.g. "charge". */
	drive: string;
	/**
	 * How the value moves across the frames. "linear" is a level that fills,
	 * "pulse" is a sine loop for anything that throbs rather than fills, which
	 * is what the hand-written RTG and fusion tap do.
	 */
	curve?: Curve;
	/** Explicit per-frame values, overriding the curve. */
	values?: number[];
	/**
	 * Also write frame N as a plain <name>.png.
	 *
	 * Several generators emit a still alongside the animation for menus and
	 * tooltips. Without this the port would silently drop a file the game loads.
	 */
	still?: number;
	/**
	 * Extra named signals, one array of per-frame values each.
	 *
	 * A single drive covers anything that fills or sweeps, but plenty of the
	 * hand-written sprites move two things independently: the chicken coop
	 * blinks an LED every two frames while shifting its straw every one. Those
	 * cannot share a drive, and approximating them away would drop the
	 * animation rather than the detail.
	 */
	drives?: Record<string, number[]>;
}

export interface SpriteSpec {
	name: string;
	size: [number, number];
	/** A registered kit's name, or a kit written out in place. */
	kit: string | KitSource;
	/** Where this belongs under assets/sprites, e.g. "items/power/battery". */
	out?: string;
	/** The generator this replaces, so coverage can be counted mechanically. */
	replaces?: string;
	/**
	 * Say what this port does NOT reproduce, and that the difference was looked
	 * at and accepted.
	 *
	 * Some generators are craft rather than composition, and the choice is
	 * between an approximation and keeping a 200-line script forever. An
	 * approximation is fine. An approximation nobody noticed is not, so it has
	 * to be written down here rather than inferred from a diff being small.
	 *
	 * A spec with this set is pinned by a golden render in fixtures/approved/,
	 * so the accepted difference cannot quietly grow later.
	 */
	approximates?: string;
	frames?: Frames;
	outline?: boolean | string;
	/**
	 * Which edges join up, for art meant to repeat: a floor, a wall, water.
	 *
	 * `true` wraps both axes. `{ x: true }` wraps only across, which is what a
	 * wall strip or a conveyor wants. Anything drawn past a wrapped edge
	 * continues on the opposite one, and the keyline pass joins up with it, so a
	 * tiled surface has no seam and no grid.
	 *
	 * This is a property of the canvas rather than an instruction to the model,
	 * on purpose. Asked to "make it tileable" a model produces something that
	 * looks tileable and is not, and nobody finds out until it is in a game.
	 * Wrapping in the renderer makes a non-tiling tile unexpressible.
	 */
	tile?: boolean | { x?: boolean; y?: boolean };
	parts: Part[];
}

export const SPEC_FIELDS = new Set(["name", "size", "kit", "out", "replaces", "approximates", "frames", "outline", "tile", "parts"]);

/** The spec's `tile` in the one shape the renderer wants. */
export function wrapOf(spec: SpriteSpec): { x: boolean; y: boolean } {
	const t = spec.tile;
	if (t === undefined || t === false) return { x: false, y: false };
	if (t === true) return { x: true, y: true };
	return { x: t.x === true, y: t.y === true };
}

export interface Part {
	shape: string;
	[field: string]: unknown;
}

/** A number, or the name of a frame drive resolved per frame. */
export type ValueRef = number | string;

/** Pixels a part moves: a fixed number, or a drive swinging it either side. */
export type ShiftRef = number | { drive: string; by: number };

export type FieldType =
	| "role"
	| "ramp"
	| "number"
	| "int"
	| "value"
	| "box"
	| "point"
	| "edge"
	| "bool"
	| "glyph"
	| "enum"
	| "roles"
	| "rows"
	| "legend";

export interface FieldDef {
	type: FieldType;
	required?: boolean;
	default?: unknown;
	/** Allowed strings, for type "enum". */
	values?: string[];
}

export type ShapeFields = Record<string, FieldDef>;

export const EDGES = ["top", "bottom", "left", "right"] as const;
export type Edge = (typeof EDGES)[number];

export interface Box {
	x0: number;
	y0: number;
	x1: number;
	y1: number;
	w: number;
	h: number;
}

// ---- resolving ---------------------------------------------------------------

/**
 * Coordinates may be negative, meaning "from the far edge", so a spec can say
 * -2 and stay correct when the same parts are reused at a different size.
 */
export function resolveCoord(v: number, extent: number): number {
	return v < 0 ? extent + v : v;
}

export function resolveBox(inset: number[], width: number, height: number): Box {
	const [l, t, r, b] = inset;
	const x0 = l;
	const y0 = t;
	const x1 = width - 1 - r;
	const y1 = height - 1 - b;
	return { x0, y0, x1, y1, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}

export interface Frame {
	index: number;
	/** Drive name to its 0..1 value for this frame. */
	values: Record<string, number>;
}

export function framesOf(spec: SpriteSpec): Frame[] {
	if (!spec.frames) return [{ index: 0, values: {} }];
	const { count, drive, values, curve, drives } = spec.frames;
	const at = (i: number) => {
		if (values) return values[i];
		if (curve === "pulse") return (1 + Math.sin((i * 2 * Math.PI) / count)) / 2;
		return count === 1 ? 1 : i / (count - 1);
	};
	return Array.from({ length: count }, (_, i) => ({
		index: i,
		values: {
			[drive]: at(i),
			...Object.fromEntries(Object.entries(drives ?? {}).map(([name, vs]) => [name, vs[i]])),
		},
	}));
}

export function resolveValue(ref: ValueRef, frame: Frame): number {
	if (typeof ref === "number") return ref;
	const v = frame.values[ref];
	if (v === undefined) {
		const known = Object.keys(frame.values);
		throw new Error(
			`"${ref}" is not a frame drive. ` +
				(known.length ? `This sprite drives: ${known.join(", ")}.` : "This sprite has no frames block."),
		);
	}
	return v;
}

// ---- validation ---------------------------------------------------------------

// Declared in error.ts so kit.ts can throw it too without a cycle.
export { SpecError } from "./error.js";

/** Every signal a part may reference by name. */
export function driveNames(frames: Frames): string[] {
	return [frames.drive, ...Object.keys(frames.drives ?? {})];
}

function fail(where: string, message: string): never {
	throw new SpecError(`${where}: ${message}`);
}

/**
 * Every part may move, and the same way whatever it is drawn with.
 *
 * Two whole numbers of pixels, either of which may name a frame drive, so a leg
 * can step and an arm can swing without a spec per pose. Applied after the part
 * has worked out where it goes, which is what keeps an inset meaning the canvas
 * edge rather than the edge of wherever the part ended up.
 */
function checkShift(where: string, raw: unknown, spec: SpriteSpec): void {
	if (!Array.isArray(raw) || raw.length !== 2) {
		fail(where, `shift expected [x, y], got ${JSON.stringify(raw)}`);
	}
	for (const entry of raw as unknown[]) {
		if (typeof entry === "number") {
			if (!Number.isInteger(entry)) fail(where, `shift moves whole pixels, got ${entry}`);
			continue;
		}

		const swing = entry as { drive?: unknown; by?: unknown };
		if (!entry || typeof entry !== "object" || typeof swing.drive !== "string") {
			fail(where, `shift expected a number of pixels or {drive, by}, got ${JSON.stringify(entry)}`);
		}
		if (!Number.isInteger(swing.by)) {
			fail(where, `shift by moves whole pixels, got ${JSON.stringify(swing.by)}`);
		}
		if (!spec.frames || !driveNames(spec.frames).includes(swing.drive as string)) {
			const known = spec.frames ? driveNames(spec.frames).join(", ") : "nothing";
			fail(where, `shift names "${String(swing.drive)}", which is not a drive. This sprite drives: ${known}`);
		}
	}
}

/**
 * How far a shift moves this frame.
 *
 * A drive is a level from nought to one, because that is what everything else
 * they feed expects, so a swing reads the middle of that range as where the part
 * was written and the two ends as `by` pixels either side of it.
 */
export function resolveShift(entry: ShiftRef, frame: Frame): number {
	if (typeof entry === "number") return entry;
	return Math.round((resolveValue(entry.drive, frame) - 0.5) * 2 * entry.by);
}

function checkField(where: string, name: string, def: FieldDef, raw: unknown, kit: Kit, spec: SpriteSpec): void {
	const at = `${where}.${name}`;
	switch (def.type) {
		case "role":
			if (typeof raw !== "string") fail(at, `expected a colour role name, got ${JSON.stringify(raw)}`);
			resolveRole(kit, raw as string);
			return;
		case "roles":
			if (!Array.isArray(raw) || raw.some((r) => typeof r !== "string")) {
				fail(at, `expected a list of colour role names, got ${JSON.stringify(raw)}`);
			}
			for (const r of raw as string[]) resolveRole(kit, r);
			return;
		case "ramp":
			if (typeof raw !== "string") fail(at, `expected a ramp name, got ${JSON.stringify(raw)}`);
			resolveRamp(kit, raw as string);
			return;
		case "rows": {
			if (!Array.isArray(raw) || raw.length === 0 || raw.some((r) => typeof r !== "string")) {
				fail(at, `expected a list of strings, one per row, got ${JSON.stringify(raw)}`);
			}
			// Ragged rows are accepted and padded, not refused. See the note in
			// art.test.ts: a model drawing twenty-five rows of ASCII miscounts, and
			// handing it the error does not help, because counting is the thing it
			// is worst at. Padding only ever adds cells to the right of a row, so a
			// pixel already placed cannot move.
			return;
		}
		case "legend": {
			if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
				fail(at, `expected an object of character to role name, got ${JSON.stringify(raw)}`);
			}
			for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
				if ([...key].length !== 1) {
					fail(at, `legend key ${JSON.stringify(key)} must be one character, or no cell can ever match it`);
				}
				if (typeof value !== "string") fail(at, `legend "${key}" must name a role, got ${JSON.stringify(value)}`);
				resolveRole(kit, value as string);
			}
			return;
		}
		case "glyph":
			if (typeof raw !== "string") fail(at, `expected a glyph name, got ${JSON.stringify(raw)}`);
			if (!KNOWN_GLYPHS.has(raw as string)) {
				fail(at, `unknown glyph "${raw}". Known glyphs: ${[...KNOWN_GLYPHS].sort().join(", ")}`);
			}
			return;
		case "enum":
			if (typeof raw !== "string" || !def.values?.includes(raw)) {
				fail(at, `expected one of ${def.values?.join(", ")}, got ${JSON.stringify(raw)}`);
			}
			return;
		case "number":
			if (typeof raw !== "number" || !Number.isFinite(raw)) fail(at, `expected a number, got ${JSON.stringify(raw)}`);
			return;
		case "int":
			if (typeof raw !== "number" || !Number.isInteger(raw)) {
				fail(at, `expected a whole number, got ${JSON.stringify(raw)}`);
			}
			return;
		case "bool":
			if (typeof raw !== "boolean") fail(at, `expected true or false, got ${JSON.stringify(raw)}`);
			return;
		case "value":
			if (typeof raw === "number") {
				if (raw < 0 || raw > 1) fail(at, `a value must be between 0 and 1, got ${raw}`);
				return;
			}
			if (typeof raw !== "string") fail(at, `expected a number 0..1 or a frame drive name, got ${JSON.stringify(raw)}`);
			if (!spec.frames || !driveNames(spec.frames).includes(raw)) {
				const known = spec.frames ? driveNames(spec.frames).join(", ") : "nothing";
				fail(at, `"${raw}" is not a frame drive. This sprite drives: ${known}.`);
			}
			return;
		case "box": {
			if (!Array.isArray(raw) || raw.length !== 4 || raw.some((n) => typeof n !== "number")) {
				fail(at, `expected [left, top, right, bottom] insets, got ${JSON.stringify(raw)}`);
			}
			const box = resolveBox(raw as number[], spec.size[0], spec.size[1]);
			if (box.w <= 0 || box.h <= 0) {
				fail(at, `insets ${JSON.stringify(raw)} leave nothing to draw in a ${spec.size[0]}x${spec.size[1]} sprite`);
			}
			return;
		}
		case "point":
			if (!Array.isArray(raw) || raw.length !== 2 || raw.some((n) => typeof n !== "number")) {
				fail(at, `expected [x, y], got ${JSON.stringify(raw)}`);
			}
			return;
		case "edge":
			if (typeof raw !== "string" || !(EDGES as readonly string[]).includes(raw)) {
				fail(at, `expected one of ${EDGES.join(", ")}, got ${JSON.stringify(raw)}`);
			}
			return;
	}
}

/**
 * Every part may be limited to some frames, which is how a pose changes shape.
 *
 * `shift` slides a part but cannot bend it, so an arm reaching out or a leg
 * stepping had no way to be drawn. A pose is its own part, shown only on its own
 * frames, while whatever does not change is written once with no `on` at all.
 */
function checkOn(where: string, raw: unknown, spec: SpriteSpec): void {
	if (!Array.isArray(raw) || !raw.length || raw.some((n) => !Number.isInteger(n))) {
		fail(where, `on expected a non-empty list of frame numbers, got ${JSON.stringify(raw)}`);
	}
	const count = spec.frames?.count ?? 1;
	const past = (raw as number[]).find((n) => n < 0 || n >= count);
	if (past !== undefined) {
		fail(where, `on lists frame ${past}, but frames.count is ${count}, so frames run 0 to ${count - 1}`);
	}
}

/**
 * Fills in defaults and rejects anything the vocabulary does not cover,
 * including field names that do not exist. A typo has to be an error: a
 * silently ignored `colour` next to a defaulted `role` renders a sprite that
 * is wrong in a way nobody will look for.
 */
export function validatePart(part: Part, fields: ShapeFields, index: number, spec: SpriteSpec, kit: Kit): Part {
	const where = `parts[${index}] (${part.shape})`;
	const out: Part = { shape: part.shape };

	for (const [name, value] of Object.entries(part)) {
		if (name === "shape") continue;
		// `shift` and `on` belong to every shape rather than to any of them, so
		// they are checked here instead of appearing in every field list that
		// would then all have to agree.
		if (name === "shift") {
			checkShift(where, value, spec);
			out.shift = value as ShiftRef[];
			continue;
		}
		if (name === "on") {
			checkOn(where, value, spec);
			out.on = value as number[];
			continue;
		}
		if (!fields[name]) {
			fail(where, `unknown field "${name}". This shape takes: ${Object.keys(fields).sort().join(", ")}`);
		}
		checkField(where, name, fields[name], value, kit, spec);
		out[name] = value;
	}
	for (const [name, def] of Object.entries(fields)) {
		if (out[name] !== undefined) continue;
		if (def.required) fail(where, `missing required field "${name}"`);
		// Defaults are deliberately NOT checked against the kit here.
		//
		// A shape's role defaults name roles from the palette it grew up in:
		// `casing` defaults its lip to `rim`. Checking them would force every
		// kit to define `rim` even for a sprite with no lip, which makes a kit
		// written for one sprite impossible. The shape only reads a default when
		// it actually draws that feature, and reaching for a role no kit defines
		// throws there, naming the roles that exist. So the check is real, it
		// just happens at the moment the answer is knowable.
		if (def.default !== undefined) out[name] = def.default;
	}
	return out;
}

/**
 * What a sprite may not exceed.
 *
 * Both are far above anything a real sprite needs: the largest example in this
 * repository is 28 parts over 8 frames. They exist because the work of
 * rendering is parts multiplied by frames, and neither had a ceiling.
 */
const MAX_PARTS = 400;
const MAX_FRAMES = 64;

export function validateSpec(spec: SpriteSpec, shapeFields: Record<string, ShapeFields>): { kit: Kit; parts: Part[] } {
	if (!spec || typeof spec !== "object") throw new SpecError("spec is not an object");
	for (const key of Object.keys(spec)) {
		if (!SPEC_FIELDS.has(key)) {
			throw new SpecError(`unknown top-level field "${key}". A spec takes: ${[...SPEC_FIELDS].sort().join(", ")}`);
		}
	}
	if (typeof spec.name !== "string" || !spec.name) throw new SpecError("spec.name is required");
	if (
		!Array.isArray(spec.size) ||
		spec.size.length !== 2 ||
		!spec.size.every((n) => Number.isInteger(n) && n > 0 && n <= 512)
	) {
		throw new SpecError(`spec.size must be [width, height] in whole pixels, got ${JSON.stringify(spec.size)}`);
	}
	if (spec.tile !== undefined && typeof spec.tile !== "boolean") {
		if (typeof spec.tile !== "object" || spec.tile === null || Array.isArray(spec.tile)) {
			throw new SpecError(`spec.tile must be true, false, or { x, y }, got ${JSON.stringify(spec.tile)}`);
		}
		for (const [axis, value] of Object.entries(spec.tile)) {
			if (axis !== "x" && axis !== "y") {
				throw new SpecError(`spec.tile takes: x, y. Got "${axis}"`);
			}
			if (typeof value !== "boolean") {
				throw new SpecError(`spec.tile.${axis} must be true or false, got ${JSON.stringify(value)}`);
			}
		}
	}
	const kit = kitFrom(spec.kit);

	/**
	 * Upper bounds, because rendering is the expensive part.
	 *
	 * `size` was already capped but `frames.count` and the length of `parts` were
	 * not, and the work done is their product. A spec asking for a million frames
	 * of a 512 square sprite is a legal spec that hangs whatever renders it, and
	 * the thing that renders it first is the SERVER: every draw is rendered once
	 * before it is returned. Nothing sensible needs more than this.
	 */
	if (!Array.isArray(spec.parts)) throw new SpecError("spec.parts must be a list");
	if (spec.parts.length > MAX_PARTS) {
		throw new SpecError(`a sprite may have up to ${MAX_PARTS} parts, got ${spec.parts.length}`);
	}

	if (spec.frames) {
		const f = spec.frames;
		if (!Number.isInteger(f.count) || f.count < 1) throw new SpecError("frames.count must be a whole number of 1 or more");
		if (f.count > MAX_FRAMES) throw new SpecError(`frames.count may be at most ${MAX_FRAMES}, got ${f.count}`);
		if (typeof f.drive !== "string" || !f.drive) throw new SpecError("frames.drive must be a name, e.g. \"charge\"");
		if (f.values && f.values.length !== f.count) {
			throw new SpecError(`frames.values has ${f.values.length} entries but frames.count is ${f.count}`);
		}
		if (f.still !== undefined && (!Number.isInteger(f.still) || f.still < 0 || f.still >= f.count)) {
			throw new SpecError(`frames.still must be a frame index between 0 and ${f.count - 1}`);
		}
		if (f.curve && !(CURVES as readonly string[]).includes(f.curve)) {
			throw new SpecError(`unknown frames.curve "${f.curve}". Known curves: ${CURVES.join(", ")}`);
		}
		for (const key of Object.keys(f)) {
			if (!["count", "drive", "curve", "values", "drives", "still"].includes(key)) {
				throw new SpecError(`unknown field "frames.${key}"`);
			}
		}
		for (const [name, vs] of Object.entries(f.drives ?? {})) {
			if (name === f.drive) throw new SpecError(`frames.drives."${name}" collides with frames.drive`);
			if (!Array.isArray(vs) || vs.length !== f.count) {
				throw new SpecError(`frames.drives."${name}" needs exactly ${f.count} values`);
			}
			if (vs.some((v) => typeof v !== "number" || v < 0 || v > 1)) {
				throw new SpecError(`frames.drives."${name}" values must all be numbers between 0 and 1`);
			}
		}
	}
	if (typeof spec.outline === "string") resolveRole(kit, spec.outline);
	if (spec.approximates !== undefined && (typeof spec.approximates !== "string" || spec.approximates.length < 10)) {
		throw new SpecError("spec.approximates must say what differs, in a sentence. An empty marker is not a review.");
	}

	if (!Array.isArray(spec.parts) || spec.parts.length === 0) {
		throw new SpecError("spec.parts must be a non-empty list");
	}

	const parts = spec.parts.map((part, i) => {
		if (!part || typeof part.shape !== "string") {
			throw new SpecError(`parts[${i}] has no shape`);
		}
		const fields = shapeFields[part.shape];
		if (!fields) {
			throw new SpecError(
				`parts[${i}]: unknown shape "${part.shape}". ` + `Known shapes: ${Object.keys(shapeFields).sort().join(", ")}`,
			);
		}
		const checked = validatePart(part, fields, i, spec, kit);
		if (part.shape.startsWith("iso_")) {
			// Against every frame it will be drawn in, because a part that fits
			// where it was written can still be walked off the edge.
			for (const frame of framesOf(spec)) checkIsoFits(checked, i, spec.size[0], spec.size[1], frame);
		}
		return checked;
	});

	return { kit, parts };
}

/**
 * An isometric part has to fit the canvas it is drawn on.
 *
 * This fails invisibly otherwise: the sprite renders, it looks almost right, and
 * the only symptom is the corner nearest the viewer sliced off along a straight
 * line. Every building in the first LingoVale set had it and nobody noticed until
 * the art was in a game. Ending exactly on the last row is fine; going past it is
 * the mistake, and the message says by how much so the fix is arithmetic.
 */
function checkIsoFits(part: Part, index: number, width: number, height: number, frame: Frame): void {
	const written = part.at as [number, number] | undefined;
	if (!written) return;

	const shift = part.shift as [ShiftRef, ShiftRef] | undefined;
	const at: [number, number] = shift
		? [written[0] + resolveShift(shift[0], frame), written[1] + resolveShift(shift[1], frame)]
		: written;

	const tile = (part.tile as number | undefined) ?? 32;
	const [w, h] = (part.tiles as [number, number] | undefined) ?? [1, 1];
	const halfW = (tile * (w + h)) / 4;
	const halfH = (tile * (w + h)) / 8;
	const up = ((part.height as number) ?? 0) + ((part.rise as number) ?? 0);

	const over: string[] = [];
	const past = (n: number) => Math.round(n * 10) / 10;
	if (at[1] + halfH > height) over.push(`bottom by ${past(at[1] + halfH - height)}`);
	if (at[1] - halfH - up < 0) over.push(`top by ${past(halfH + up - at[1])}`);
	if (at[0] - halfW < 0) over.push(`left by ${past(halfW - at[0])}`);
	if (at[0] + halfW > width) over.push(`right by ${past(at[0] + halfW - width)}`);

	if (over.length > 0) {
		throw new SpecError(
			`parts[${index}]: ${part.shape} runs off the ${over.join(", and the ")}. ` +
				`Move its at or make the canvas bigger.`,
		);
	}
}

export function role(kit: Kit, name: unknown): Rgba {
	return resolveRole(kit, String(name));
}
